import { NodeSpec } from '@progress/kendo-react-editor';

const iframe: NodeSpec = {
  // NodeSpec interface
  // http://prosemirror.net/docs/ref/#model.NodeSpec
  attrs: {
    src: { default: '' },
    width: { default: '' },
    height: { default: '' },
    style: { default: '' }
  },
  group: 'block',
  selectable: false,
  parseDOM: [
    {
      tag: 'iframe',
      getAttrs: (dom: any) => ({
        src: dom.getAttribute('src'),
        width: dom.getAttribute('width'),
        height: dom.getAttribute('height'),
        style: dom.getAttribute('style')
      })
    }
  ],
  toDOM: (node) => {
    const attrs = {
      src: node.attrs.src,
      style: node.attrs.style,
      frameborder: '0',
      allowfullscreen: 'true',
      width: node.attrs.width,
      height: node.attrs.height
    };
    return ['iframe', attrs];
  }
};

export const vimeo: NodeSpec = {
  attrs: {
    videoId: { default: null },
    width: { default: 560 },
    height: { default: 315 },
    align: { default: 'center' },
    'fit-to-screen': { default: false }
  },
  group: 'block',
  selectable: true,
  atom: true,
  draggable: true,
  parseDOM: [
    {
      priority: 100,
      tag: 'div.video-embed',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const fit = el.getAttribute('fit-to-screen');

        return {
          videoId: el.getAttribute('data-video-id'),
          width: el.getAttribute('width'),
          height: el.getAttribute('height'),
          align: el.style.textAlign || 'center',
          ['fit-to-screen']: fit === 'true'
        };
      }
    }
  ],
  toDOM: (node) => {
    const id = node.attrs.videoId;
    const isFit =
      node.attrs['fit-to-screen'] === true ||
      node.attrs['fit-to-screen'] === 'true';
    const embedUrl = `https://player.vimeo.com/video/${id}`;

    return [
      'div',
      {
        class: 'video-embed',
        'data-video-id': id,
        width: isFit ? '100%' : node.attrs.width,
        height: isFit ? 'auto' : node.attrs.height,
        'fit-to-screen': isFit,
        style: `text-align:${node.attrs.align};width:100%;`,
        contentEditable: false
      },
      [
        'iframe',
        {
          src: embedUrl,
          width: isFit ? '100%' : node.attrs.width,
          height: isFit ? 'auto' : node.attrs.height,
          frameborder: '0',
          allowfullscreen: 'true',
          referrerpolicy: 'strict-origin-when-cross-origin',
          allow:
            'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
        }
      ]
    ];
  }
};

export const youtube: NodeSpec = {
  attrs: {
    videoId: { default: null },
    width: { default: 560 },
    height: { default: 315 },
    align: { default: 'center' },
    'fit-to-screen': { default: false }
  },
  group: 'block',
  selectable: true,
  atom: true,
  draggable: true,
  parseDOM: [
    {
      priority: 100,
      tag: 'div.video-embed.youtube',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const fit = el.getAttribute('fit-to-screen');

        return {
          videoId: el.getAttribute('data-video-id'),
          width: el.getAttribute('width'),
          height: el.getAttribute('height'),
          align: el.style.textAlign || 'center',
          ['fit-to-screen']: fit === 'true'
        };
      }
    }
  ],
  toDOM: (node) => {
    const id = node.attrs.videoId;
    const isFit =
      node.attrs['fit-to-screen'] === true ||
      node.attrs['fit-to-screen'] === 'true';
    const thumbnail = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    const watchUrl = `https://www.youtube.com/watch?v=${id}`;
    const embedUrl = `https://www.youtube.com/embed/${id}`;

    return [
      'div',
      {
        class: 'video-embed youtube',
        'data-video-id': id,
        width: isFit ? '100%' : node.attrs.width,
        height: isFit ? 'auto' : node.attrs.height,
        'fit-to-screen': isFit,
        style: `text-align:${node.attrs.align};width:100%;`,
        contentEditable: false
      },
      [
        'a',
        {
          href: watchUrl,
          target: '_blank',
          rel: 'noopener',
          style: `display:flex;flex-direction:column;text-decoration:none;color:black;width:100%;`
        },
        [
          'img',
          {
            src: thumbnail,
            width: isFit ? '100%' : node.attrs.width,
            height: isFit ? 'auto' : node.attrs.height,
            style: `object-fit:contain;align-self:center;`,
            alt: node.attrs.alt ?? 'Youtube video'
          }
        ],
        ['span', {}, 'Open video↗']
      ],
      [
        'div',
        {
          class: 'video-iframe',
          style: 'display:none'
        },
        [
          'iframe',
          {
            src: embedUrl,
            width: isFit ? '100%' : node.attrs.width,
            height: isFit ? 'auto' : node.attrs.height,
            frameborder: '0',
            allowfullscreen: 'true',
            referrerpolicy: 'strict-origin-when-cross-origin',
            allow:
              'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
          }
        ]
      ]
    ];
  }
};

const video: NodeSpec = {
  attrs: {
    src: { default: '' },
    alt: { default: '' },
    title: { default: '' },
    width: { default: '' },
    height: { default: '' },
    style: { default: '' },
    align: { default: 'center' },
    'fit-to-screen': { default: false }
  },
  group: 'block',
  parseDOM: [
    {
      tag: 'video',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const fit = el.getAttribute('fit-to-screen');
        return {
          src: el.getAttribute('src'),
          width: el.getAttribute('width'),
          height: el.getAttribute('height'),
          style: el.getAttribute('style'),
          alt: el.getAttribute('alt'),
          title: el.getAttribute('title'),
          align: el.getAttribute('align') || 'center',
          ['fit-to-screen']: fit === 'true'
        };
      }
    }
  ],
  toDOM: (node) => {
    const isFit = node.attrs['fit-to-screen'] === 'true';
    return [
      'div',
      {
        style: `text-align:${node.attrs.align}; ${
          isFit ? 'width:100%;height:auto;overflow:hidden;' : ''
        }`
      },
      [
        'video',
        {
          src: node.attrs.src,
          alt: node.attrs.alt,
          title: node.attrs.title,
          controls: true,
          width: isFit ? '100%' : node.attrs.width,
          height: isFit ? 'auto' : node.attrs.height,
          ['fit-to-screen']: isFit
        }
      ]
    ];
  }
};

const audio: NodeSpec = {
  attrs: {
    src: { default: '' },
    alt: { default: '' },
    title: { default: '' },
    width: { default: '' },
    height: { default: '' },
    style: { default: '' },
    align: { default: 'center' }
  },
  group: 'block',
  parseDOM: [
    {
      tag: 'audio',
      getAttrs: (dom) => ({
        src: (dom as HTMLElement).getAttribute('src'),
        style: (dom as HTMLElement).getAttribute('style'),
        alt: (dom as HTMLElement).getAttribute('alt'),
        title: (dom as HTMLElement).getAttribute('title'),
        align: (dom as HTMLElement).getAttribute('align') || 'center'
      })
    }
  ],
  toDOM: (node) => {
    const attrs = {
      src: node.attrs.src,
      alt: node.attrs.alt,
      title: node.attrs.title,
      controls: true,
      style: node.attrs.style
    };
    return [
      'div',
      { style: `text-align: ${node.attrs.align};` },
      ['audio', attrs]
    ];
  }
};

export { iframe, video, audio };
